import { useEffect, useMemo, useState } from 'react';
import itemsData from '@/data/items.json';
import { useTradeStore } from '@/state/tradeStore';
import { useUIStore } from '@/state/uiStore';
import { useCharacterStore } from '@/state/characterStore';
import { Trade, type TradeItemDTO, type TradeSideDTO } from '@/network/RestApi';
import { inventory, type InventoryItem } from '@/systems/InventorySystem';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

interface ItemTpl {
  id: number;
  name: string;
  type: string;
  icon?: string;
}
const TPL: Record<number, ItemTpl> = Object.fromEntries(
  (itemsData.items as ItemTpl[]).map((i) => [i.id, i]),
);

const SLOT_SIZE = 48;
const COLS = 4;
const ROWS = 3; // 12 slots = MaxItemsPerSide

export function TradePanel() {
  const session = useTradeStore((s) => s.session);
  const incoming = useTradeStore((s) => s.incoming);
  const refresh = useTradeStore((s) => s.refresh);
  const removeIncoming = useTradeStore((s) => s.removeIncoming);
  const close = () => useUIStore.getState().set('trade', false);
  const showToast = useUIStore((s) => s.showToast);
  const ch = useCharacterStore((s) => s.current);
  const [busy, setBusy] = useState(false);
  const [zenInput, setZenInput] = useState('');
  const [invItems, setInvItems] = useState<InventoryItem[]>([]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load inventory list when panel mounts.
  useEffect(() => {
    if (!ch) return;
    inventory.setChar(ch.id);
    inventory.refresh()
      .then((list) => setInvItems(list.filter((it) => (it.slotType ?? 'inventory') === 'inventory')))
      .catch(() => { /* ignore */ });
  }, [ch, session?.id]);

  // Sync zen input field with server state when other side has changed it
  // or when our lock just got reset.
  useEffect(() => {
    if (session) setZenInput(String(session.me.zen));
  }, [session?.id, session?.me.zen]);

  if (!ch) return null;

  const wrap = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  // Pending incoming requests view (no active session yet).
  if (!session) {
    return (
      <div style={panelStyle}>
        <Header title="Giao Dịch" onClose={close} />
        {incoming.length === 0 ? (
          <div style={{ opacity: 0.7, padding: 12 }}>Chưa có lời mời giao dịch nào.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {incoming.map((r) => (
              <div key={r.requestId} style={requestRow}>
                <span><b>{r.fromName}</b> muốn giao dịch với bạn</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    style={ui.primaryBtn}
                    disabled={busy}
                    onClick={() => wrap(async () => {
                      try {
                        await Trade.accept(ch.id, { requestId: r.requestId });
                        removeIncoming(r.requestId);
                        await refresh();
                      } catch (e) { showToast((e as Error).message, 'error'); }
                    })}
                  >Chấp nhận</button>
                  <button
                    style={ui.secondaryBtn}
                    disabled={busy}
                    onClick={() => wrap(async () => {
                      try { await Trade.reject(ch.id, { requestId: r.requestId }); }
                      catch (e) { showToast((e as Error).message, 'error'); }
                      removeIncoming(r.requestId);
                    })}
                  >Từ chối</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const me = session.me;
  const other = session.other;
  const otherInTrade = new Set(other.items.map((i) => i.itemId));
  const myInTrade = new Set(me.items.map((i) => i.itemId));
  const availableItems = invItems.filter((it) => !myInTrade.has(it.id));

  const onAdd = (itemId: number) => wrap(async () => {
    try { await Trade.add(ch.id, { itemId }); await refresh(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onRemove = (itemId: number) => wrap(async () => {
    try { await Trade.remove(ch.id, { itemId }); await refresh(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onSetZen = () => wrap(async () => {
    const amount = Math.max(0, Math.floor(Number(zenInput) || 0));
    try { await Trade.zen(ch.id, { amount }); await refresh(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onLock = () => wrap(async () => {
    try { await Trade.lock(ch.id); await refresh(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onUnlock = () => wrap(async () => {
    try { await Trade.unlock(ch.id); await refresh(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onConfirm = () => wrap(async () => {
    try {
      const r = await Trade.confirm(ch.id);
      if (r.completed) showToast('Giao dịch hoàn tất', 'info');
    } catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onCancel = () => wrap(async () => {
    try { await Trade.cancel(ch.id); }
    catch (e) { showToast((e as Error).message, 'error'); }
  });

  const statusLabel = session.status === 1 ? 'Đã khoá - chờ xác nhận' : 'Đang chỉnh sửa';

  return (
    <div style={{ ...panelStyle, width: 720 }}>
      <Header title={`Giao Dịch · ${statusLabel}`} onClose={close} />

      <div style={{ display: 'flex', gap: 12 }}>
        <SideColumn
          title={`Bạn (${me.name})`}
          side={me}
          onSlotClick={(it) => onRemove(it.itemId)}
          color="#4cd1ff"
        />
        <SideColumn
          title={`Đối phương (${other.name})`}
          side={other}
          onSlotClick={() => { /* readonly */ }}
          color="#ffd166"
          readonly
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <span style={{ minWidth: 60 }}>Zen của bạn:</span>
        <input
          style={{ ...ui.input, width: 140 }}
          type="number"
          min={0}
          value={zenInput}
          onChange={(e) => setZenInput(e.target.value)}
          disabled={me.locked || busy}
        />
        <button style={ui.secondaryBtn} onClick={onSetZen} disabled={me.locked || busy}>Đặt</button>
        <span style={{ marginLeft: 16, opacity: 0.8 }}>Zen đối phương đề xuất: <b>{other.zen}</b></span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        {!me.locked ? (
          <button style={ui.primaryBtn} onClick={onLock} disabled={busy}>Khoá đề xuất</button>
        ) : (
          <button style={ui.secondaryBtn} onClick={onUnlock} disabled={busy}>Bỏ khoá</button>
        )}
        <button
          style={{ ...ui.primaryBtn, opacity: session.status === 1 && !me.confirmed ? 1 : 0.5 }}
          onClick={onConfirm}
          disabled={busy || session.status !== 1 || me.confirmed}
        >
          {me.confirmed ? `Đã xác nhận - chờ ${other.name}` : 'Xác nhận giao dịch'}
        </button>
        <button style={ui.secondaryBtn} onClick={onCancel} disabled={busy}>Huỷ phiên</button>
      </div>

      <div style={{ marginTop: 16, borderTop: '1px solid #2c3e50', paddingTop: 12 }}>
        <div style={{ marginBottom: 6, fontWeight: 700 }}>
          Túi đồ của bạn (click để thêm vào giao dịch)
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(8, ${SLOT_SIZE}px)`,
          gap: 4,
          maxHeight: 240,
          overflowY: 'auto',
        }}>
          {availableItems.map((it) => {
            const tpl = TPL[it.templateId];
            return (
              <button
                key={it.id}
                title={`${tpl?.name ?? '?'} ×${it.quantity}${it.upgradeLevel ? ` +${it.upgradeLevel}` : ''}`}
                onClick={() => onAdd(it.id)}
                disabled={me.locked || busy || otherInTrade.has(it.id)}
                style={{
                  ...invSlotStyle,
                  cursor: me.locked ? 'not-allowed' : 'pointer',
                  opacity: me.locked ? 0.4 : 1,
                }}
              >
                <span style={{ fontSize: 11, lineHeight: 1.1 }}>
                  {tpl?.name ?? `#${it.templateId}`}
                </span>
                {it.quantity > 1 && (
                  <span style={qtyBadge}>{it.quantity}</span>
                )}
              </button>
            );
          })}
          {availableItems.length === 0 && (
            <span style={{ gridColumn: '1 / -1', opacity: 0.6 }}>Túi đồ trống.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
      borderBottom: '1px solid #2c3e50',
      paddingBottom: 6,
    }}>
      <span style={{ fontWeight: 700, color: '#ffd166' }}>{title}</span>
      <CloseButton onClick={onClose} />
    </div>
  );
}

function SideColumn({
  title, side, onSlotClick, color, readonly,
}: {
  title: string;
  side: TradeSideDTO;
  onSlotClick: (it: TradeItemDTO) => void;
  color: string;
  readonly?: boolean;
}) {
  const slots = useMemo(() => {
    const arr: (TradeItemDTO | null)[] = Array(COLS * ROWS).fill(null);
    side.items.slice(0, COLS * ROWS).forEach((it, i) => { arr[i] = it; });
    return arr;
  }, [side.items]);

  return (
    <div style={{ flex: 1, border: `1px solid ${color}`, borderRadius: 6, padding: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color }}>{title}</span>
        <span style={{ fontSize: 11 }}>
          {side.locked && <span style={{ color: '#ffd166', marginRight: 6 }}>● Locked</span>}
          {side.confirmed && <span style={{ color: '#2ecc71' }}>✓ Confirmed</span>}
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, ${SLOT_SIZE}px)`,
        gap: 4,
      }}>
        {slots.map((it, i) => (
          <div
            key={i}
            onClick={() => it && !readonly && onSlotClick(it)}
            style={{
              ...invSlotStyle,
              cursor: it && !readonly ? 'pointer' : 'default',
              borderColor: it ? color : '#324a5e',
            }}
            title={it ? `${TPL[it.templateId]?.name ?? '?'} ×${it.quantity}` : ''}
          >
            {it && (
              <>
                <span style={{ fontSize: 11, lineHeight: 1.1 }}>
                  {TPL[it.templateId]?.name ?? `#${it.templateId}`}
                </span>
                {it.upgradeLevel > 0 && (
                  <span style={{ fontSize: 10, color: '#ffd166' }}>+{it.upgradeLevel}</span>
                )}
                {it.quantity > 1 && <span style={qtyBadge}>{it.quantity}</span>}
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 13 }}>
        Zen: <b>{side.zen}</b>
      </div>
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  ...ui.panel,
  position: 'absolute',
  top: 80,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 560,
  zIndex: 50,
};

const requestRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 8,
  background: 'rgba(50,74,94,0.4)',
  borderRadius: 4,
};

const invSlotStyle: React.CSSProperties = {
  width: SLOT_SIZE,
  height: SLOT_SIZE,
  background: '#0b1220',
  border: '1px solid #324a5e',
  borderRadius: 4,
  color: '#e8eef7',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: 2,
  overflow: 'hidden',
  flexDirection: 'column',
};

const qtyBadge: React.CSSProperties = {
  position: 'absolute',
  bottom: 1,
  right: 3,
  fontSize: 10,
  background: '#000a',
  padding: '0 3px',
  borderRadius: 2,
};
